import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import api from '../../../api/api';
import { money } from '../../../client/ClientReturn';

const labels = { queued: 'Запрос сохранён', processing: 'Ожидаем банк', uncertain: 'Уточняем результат', review: 'Нужна сверка с Ozon', completed: 'Деньги возвращены', failed: 'Банк отклонил возврат' };
const when = value => value ? new Date(value).toLocaleString('ru-RU') : '—';

export default function BankRefundsPanel({ dealId, refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('Досрочная сдача вещи');
  const [receipt, setReceipt] = useState(null);
  const [fields, setFields] = useState({});
  const request = useRef(null);
  const activeDeal = useRef(dealId);
  activeDeal.current = dealId;
  const url = `/rental/deals/${dealId}/bank-refunds`;
  const load = useCallback(async () => {
    if (!dealId || dealId === 'new') return;
    try {
      const res = await api.get(url, { skipLoading: true });
      if (activeDeal.current === dealId) { setData(res.data); setError(''); }
    } catch (e) {
      if (activeDeal.current === dealId) setError(e.response?.data?.detail || 'Не удалось загрузить оплаты и чеки.');
    }
  }, [dealId, url]);
  useEffect(() => { setData(null); setConfirm(false); setReceipt(null); request.current = null; load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!data?.pending) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [data?.pending, load]);
  if (!dealId || dealId === 'new') return null;
  const payment = data?.payments?.find(p => p.id === paymentId);
  const valid = Number(amount) > 0 && Number(amount) <= Number(payment?.available) && reason.trim().length >= 3;
  const run = async action => {
    if (busy) return;
    setBusy(true); setError('');
    try { await action(); }
    catch (e) { setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Ответ не получен. Обновите статус; не создавайте повторную выплату.'); }
    finally { setBusy(false); }
  };
  const submit = () => run(async () => {
    if (!request.current) request.current = { request_id: window.crypto.randomUUID(), payment_id: paymentId, amount, reason: reason.trim() };
    try { await api.post(url, request.current, { skipLoading: true }); }
    catch (e) { await load(); throw e; }
    if (activeDeal.current !== dealId) return;
    setConfirm(false); request.current = null;
    await load(); await onChanged?.();
  });
  const sync = () => run(async () => {
    const res = await api.post(`${url}/sync`, {}, { skipLoading: true });
    if (activeDeal.current !== dealId) return;
    setData(res.data); setError(res.data.sync_warning || ''); await onChanged?.();
  });
  return <Box sx={{ p: 2, border: '1px solid #eadbc9', borderRadius: 2, my: 2 }}>
    <Typography variant="h6">Оплаты, возвраты и кассовые чеки</Typography>
    {error && <Alert severity="warning" sx={{ my: 1 }}>{error}</Alert>}
    <Button onClick={sync} disabled={busy}>Обновить статус Ozon</Button>
    {data && <>
      <Typography variant="body2">Получено {money(data.paid_total)} · возвращено {money(data.refunded_total)}</Typography>
      <Typography fontWeight={700} sx={{ my: 1 }}>Доступно к возврату: {money(data.available)}</Typography>
      {!data.live_enabled && <Alert severity="info">Банковские возвраты выключены настройками Ozon.</Alert>}
      <Button variant="contained" disabled={busy || !data.live_enabled || data.pending || !data.payments?.some(p => p.available > 0)} onClick={() => {
        const selected = data.payments.find(p => p.available > 0);
        if (request.current) {
          setPaymentId(request.current.payment_id); setAmount(request.current.amount); setReason(request.current.reason);
        } else {
          setPaymentId(selected.id); setAmount(String(selected.available)); setReason('Досрочная сдача вещи');
        }
        setConfirm(true);
      }}>Вернуть через Ozon</Button>
      {!data.available && !data.pending && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Сумма появится после приёма и проверки вещи либо отмены оплаченной заявки до выдачи.</Typography>}
      {data.refunds?.map(row => <Box key={row.id} sx={{ mt: 2 }}>
        <Chip size="small" label={labels[row.status] || row.status} color={row.status === 'completed' ? 'success' : 'default'} />
        <Typography variant="body2">{money(row.amount)} · {when(row.created_at)} · сотрудник {row.actor}</Typography>
        <Typography variant="body2">{row.reason}</Typography>
        {row.message && <Typography variant="caption" display="block">{row.message}</Typography>}
      </Box>)}
      <Typography fontWeight={700} sx={{ mt: 2 }}>История кассовых чеков</Typography>
      {data.payments?.some(p => p.fiscalization_type === 'FISCAL_TYPE_DOUBLE') && <Alert severity="warning" sx={{ my: 1 }}>Для этой оплаты настроен чек предоплаты и окончательного расчёта. Финальный чек нужно оформить в Ozon/ОФД; ERP автоматически его не пробивает.</Alert>}
      <Typography variant="caption" color="text.secondary">Ozon Checkout не передаёт ссылки и реквизиты чеков через API. Сохраните готовый документ из Ozon/ОФД. Это не пробитие нового чека.</Typography>
      {data.receipts?.map(row => <Box key={row.id} sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
        <Typography variant="body2">{row.kind === 'refund' ? 'Возврат прихода' : 'Приход'} · {money(row.amount)} · {when(row.date)}</Typography>
        {row.documents?.map(doc => <Box key={doc.id}>
          <Typography variant="caption" display="block">ФН {doc.fiscal_drive} · ФД {doc.fiscal_document} · ФП {doc.fiscal_sign}</Typography>
          <Typography variant="caption" display="block">Чек от {when(doc.issued_at)} · {money(doc.amount)} · реквизиты внесены сотрудником</Typography>
          {doc.url && <Button component="a" href={doc.url} target="_blank" rel="noopener noreferrer" size="small">Открыть чек</Button>}
        </Box>)}
        {row.fiscal_status !== 'recorded' && <>
          <Typography variant="caption" color="warning.main" display="block">Чек не подтверждён в ERP — проверьте Ozon/ОФД.</Typography>
          <Button size="small" disabled={busy} onClick={() => { setReceipt(row); setFields({ amount: String(row.undocumented_amount ?? row.amount), fiscal_drive: '', fiscal_document: '', fiscal_sign: '', issued_at: '', url: '' }); }}>Сохранить реквизиты чека</Button>
        </>}
      </Box>)}
    </>}
    <Dialog open={confirm} onClose={() => !busy && setConfirm(false)} fullWidth maxWidth="sm">
      <DialogTitle>Возврат денег клиенту</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="info">Деньги вернутся по исходной оплате. Проверьте сумму: подтверждение отправит запрос в банк.</Alert>
        <TextField select label="Исходная оплата" value={paymentId} disabled={busy || !!request.current} onChange={e => {
          setPaymentId(e.target.value); setAmount(String(data.payments.find(p => p.id === e.target.value).available));
        }}>{data?.payments?.filter(p => p.available > 0).map(p => <MenuItem key={p.id} value={p.id}>{when(p.created_at)} · {money(p.amount)}</MenuItem>)}</TextField>
        <TextField label="Сумма возврата, ₽" type="number" value={amount} disabled={busy || !!request.current} inputProps={{ min: 0.01, max: payment?.available, step: 0.01 }} onChange={e => setAmount(e.target.value)} helperText={`Не больше ${money(payment?.available)}`} />
        <TextField label="Причина возврата" value={reason} disabled={busy || !!request.current} onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 500 }} />
        {error && <Alert severity="warning">{error}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setConfirm(false)}>Отмена</Button><Button variant="contained" disabled={busy || !valid} onClick={submit}>Вернуть {money(amount)}</Button></DialogActions>
    </Dialog>
    <Dialog open={!!receipt} onClose={() => !busy && setReceipt(null)} fullWidth maxWidth="sm">
      <DialogTitle>Реквизиты готового чека · {money(receipt?.amount)}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="info">Перенесите данные из чека Ozon/ОФД. Проверьте сумму и тип расчёта; запись останется в истории.</Alert>
        <TextField label="Сумма чека, ₽" type="number" value={fields.amount || ''} disabled={busy} inputProps={{ min: 0.01, max: receipt?.undocumented_amount, step: 0.01 }} onChange={e => setFields(v => ({ ...v, amount: e.target.value }))} />
        {[['fiscal_drive', 'ФН (16 цифр)'], ['fiscal_document', 'ФД'], ['fiscal_sign', 'ФП'], ['issued_at', 'Дата и время чека'], ['url', 'Ссылка на чек (необязательно)']].map(([key, label]) => <TextField key={key} label={label} value={fields[key] || ''} disabled={busy} type={key === 'issued_at' ? 'datetime-local' : 'text'} InputLabelProps={{ shrink: true }} onChange={e => setFields(v => ({ ...v, [key]: e.target.value }))} />)}
        {error && <Alert severity="warning">{error}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setReceipt(null)}>Отмена</Button><Button variant="contained" disabled={busy || !fields.fiscal_drive || !fields.fiscal_document || !fields.fiscal_sign || !fields.issued_at} onClick={() => run(async () => {
        await api.post(`${url}/receipts/${receipt.id}`, { ...fields, issued_at: new Date(fields.issued_at).toISOString(), url: fields.url || null });
        setReceipt(null); await load(); await onChanged?.();
      })}>Сохранить</Button></DialogActions>
    </Dialog>
  </Box>;
}
